import { memo, useMemo } from 'react'
import PropTypes from 'prop-types'

import { Colors } from '@aytm/app/stats/lib'

import { MODE_TYPES, SETTINGS } from '@aytm/app/stats/page/question-views/psc/lib/constants'
import { useTweenedSegments } from '@aytm/app/stats/page/question-views/psc/states/useTweenedSegments'
import {
  fitLabel,
  generateRowData,
  getLabelsColor,
  parseText,
  pointIdOf,
  POLE_CENTER,
  POLE_LEFT,
  POLE_RIGHT,
  stripTags
} from '@aytm/app/stats/page/question-views/psc/lib/utils'

// styles
import style from '@aytm/app/stats/page/question-views/psc/components/styles/Chart.module.scss'

// Right-pole differentiation: the right pole renders a darkened variant of the
// batch colour so the two poles are distinguishable within a batch (every mode).
const RIGHT_POLE_SHIFT = [0, 0, -6]

// How far the echo a table row casts reaches past the segment it names. The same spread the
// Highcharts blocks give theirs, so a polarity bar answers a hovered row the way a column does.
const HALO_SPREAD = 2

const TRACK_COLOR = '#eceeef'
const NEUTRAL_COLOR = '#4d4d4d'
// Central-gap spacer fill in intensity modes (the neutral segment is the centre segment only in discrete mode).
const SPACER_COLOR = '#ffffff'

// A bar segment rounded on its outer end only: the edge meeting the neighbouring
// segment stays square so adjacent segments join flush. The radius is clamped to
// the segment width so sub-radius values do not bulge past their own end.
const barPath = ({ x, w, h, r, side }) => {
  if (0 >= w) return null

  const rr = Math.min(r, w)

  if ('left' === side) {
    return `M${x + rr},0 H${x + w} V${h} H${x + rr} A${rr},${rr} 0 0 1 ${x},${h - rr} V${rr} A${rr},${rr} 0 0 1 ${
      x + rr
    },0 Z`
  }

  if ('right' === side) {
    return `M${x},0 H${x + w - rr} A${rr},${rr} 0 0 1 ${x + w},${rr} V${h - rr} A${rr},${rr} 0 0 1 ${
      x + w - rr
    },${h} H${x} Z`
  }

  return `M${x},0 H${x + w} V${h} H${x} Z`
}

const LABEL_FONT_FAMILY = 'Ubuntu, Calibri, Segoe, Helvetica, Arial'
// Usable text width inside a pole-label gutter (gutter width less the inner 8px inset).
const LABEL_MAX_WIDTH = SETTINGS.poleLabelWidth - 8

// A pole label rendered as one or two lines. When wrapped or truncated, a hidden
// full-text element surfaces the un-truncated string on hover (math sub-spec §11).
const PoleLabel = ({ fit, x, anchor, fill }) => {
  const common = {
    x,
    fill,
    textAnchor: anchor,
    dominantBaseline: 'middle',
    fontFamily: LABEL_FONT_FAMILY,
    style: { fontSize: 14 }
  }

  if (fit.lines.length <= 1) {
    return (
      <text {...common} y={0}>
        {fit.lines[0] || ''}
        {fit.truncated ? <title>{fit.full}</title> : null}
      </text>
    )
  }

  return (
    <>
      <text {...common} y={-6}>
        {fit.lines[0]}
        <title>{fit.full}</title>
      </text>
      <text {...common} y={8}>
        {fit.lines[1]}
        <title>{fit.full}</title>
      </text>
    </>
  )
}

PoleLabel.propTypes = {
  fit: PropTypes.object,
  x: PropTypes.number,
  anchor: PropTypes.string,
  fill: PropTypes.string
}

const ChartRow = ({ id, width, mode, batch, data, palette, percentDecimals, onEnter, onLeave }) => {
  const y = useMemo(
    () =>
      SETTINGS.chartMargin +
      (batch.position - 1) * (SETTINGS.barHeight + 2 * SETTINGS.barMargin) +
      SETTINGS.barHeight / 2 +
      SETTINGS.barMargin,
    [batch.position]
  )

  const target = useMemo(
    () => generateRowData({ width, mode, data, percentDecimals }),
    [width, mode, data, percentDecimals]
  )
  const [left, center, right] = useTweenedSegments(target, width)

  const { leftColor, rightColor } = useMemo(() => getLabelsColor({ mode, data }), [mode, data])

  // Cycle the palette by batch index so batches beyond the palette length repeat colours.
  const leftBase = palette?.length ? palette[(batch.position - 1) % palette.length] : undefined
  const rightBase = useMemo(() => Colors.transformHex(leftBase, RIGHT_POLE_SHIFT), [leftBase])

  // The pole pair may arrive JSON-encoded; parse it the way the data and export paths do
  // so an unparseable value blanks both labels instead of indexing raw characters.
  const [leftText, rightText] = useMemo(() => parseText(batch.text), [batch.text])

  const leftFit = useMemo(() => fitLabel(stripTags(leftText), LABEL_MAX_WIDTH), [leftText])
  const rightFit = useMemo(() => fitLabel(stripTags(rightText), LABEL_MAX_WIDTH), [rightText])

  const segments = [
    { seg: left, side: 'left', pole: POLE_LEFT, fill: leftBase },
    { seg: center, side: 'none', pole: POLE_CENTER, fill: mode === MODE_TYPES.d ? NEUTRAL_COLOR : SPACER_COLOR },
    { seg: right, side: 'right', pole: POLE_RIGHT, fill: rightBase }
  ]

  return (
    <g transform={`translate(0,${y})`}>
      <PoleLabel fit={leftFit} x={SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap - 8} anchor="end" fill={leftColor} />
      <rect
        x={SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap}
        y={-SETTINGS.barHeight / 2}
        width={width - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap)}
        height={SETTINGS.barHeight}
        rx={SETTINGS.barRadius}
        fill={TRACK_COLOR}
      />
      <PoleLabel
        fit={rightFit}
        x={width - SETTINGS.poleLabelWidth - SETTINGS.poleLabelGap + 8}
        anchor="start"
        fill={rightColor}
      />

      <g transform={`translate(${SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap},${-SETTINGS.barHeight / 2})`}>
        {/* Held ready under the bars, one per segment, and switched on by the row that names it --
            so only the ring reaching past the bar shows, the way the Highcharts halo does. Outside
            the track clip: the ring stands taller than the track it hugs. */}
        <g transform={`translate(0,${-HALO_SPREAD})`}>
          {segments.map(({ seg, side, fill, pole }) => {
            // a segment with no width draws no bar, so it has nothing to echo either
            const d =
              0 >= seg.w
                ? null
                : barPath({
                    x: seg.x - HALO_SPREAD,
                    w: seg.w + 2 * HALO_SPREAD,
                    h: SETTINGS.barHeight + 2 * HALO_SPREAD,
                    r: SETTINGS.barRadius + HALO_SPREAD,
                    side
                  })

            return d ? (
              <path
                key={side}
                className={style.halo}
                data-point-id={pointIdOf(batch.position, pole)}
                d={d}
                fill={fill}
              />
            ) : null
          })}
        </g>
        <g clipPath={`url(#group-${id}-track-clip)`}>
          {segments.map(({ seg, side, fill, pole }) => {
            const d = barPath({ x: seg.x, w: seg.w, h: SETTINGS.barHeight, r: SETTINGS.barRadius, side })
            // outside the discrete view the centre is the gap between the poles, not a bar: it has
            // nothing to say, so it takes no hover, as master's white spacers did
            const hoverable = POLE_CENTER !== pole || MODE_TYPES.d === mode

            return d ? (
              <path
                key={side}
                data-testid={`stat-psc-bar-${pointIdOf(batch.position, pole)}`}
                d={d}
                fill={fill}
                onMouseEnter={
                  hoverable
                    ? event => onEnter(event.currentTarget, seg.title, seg.value, pointIdOf(batch.position, pole))
                    : undefined
                }
                onMouseLeave={hoverable ? onLeave : undefined}
              />
            ) : null
          })}
        </g>
      </g>
    </g>
  )
}

export default memo(ChartRow)

ChartRow.propTypes = {
  id: PropTypes.string,
  width: PropTypes.number,
  mode: PropTypes.string,
  batch: PropTypes.object,
  data: PropTypes.object,
  palette: PropTypes.array,
  percentDecimals: PropTypes.number,
  onEnter: PropTypes.func,
  onLeave: PropTypes.func
}
